import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Users, Send, AlertTriangle, DollarSign, Wallet, FileCheck,
  FileText, CreditCard, Mail, Gift, FileBadge, UserCheck,
  Clock, TrendingUp, ArrowUpRight, CircleDot
} from 'lucide-react';

export default function AdminDashboardPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.getAdminDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const mainCards = [
    {
      title: t('admin_dash_total_users'),
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-500/10',
      border: 'border-blue-200',
      link: '/admin/users'
    },
    {
      title: t('admin_dash_total_transfers'),
      value: stats?.total_transfers || 0,
      sub: stats?.pending_transfers > 0 ? `${stats.pending_transfers} ${t('pending')}` : null,
      subColor: 'text-amber-600',
      icon: Send,
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-200',
      link: '/admin/transfers'
    },
    {
      title: t('admin_dash_total_volume'),
      value: `¥${(stats?.total_volume_jpy || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-500/10',
      border: 'border-purple-200',
      link: '/admin/transfers'
    },
    {
      title: t('admin_dash_deposits'),
      value: stats?.total_deposits || 0,
      sub: stats?.pending_deposits > 0 ? `${stats.pending_deposits} ${t('pending')}` : null,
      subColor: 'text-amber-600',
      icon: Wallet,
      color: 'text-cyan-600',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-200',
      link: '/admin/deposits'
    },
  ];

  const docCards = [
    {
      title: t('admin_nav_kyc'),
      total: stats?.total_kyc || 0,
      pending: stats?.pending_kyc || 0,
      icon: FileCheck,
      color: 'text-orange-600',
      bg: 'bg-orange-500/10',
      link: '/admin/kyc'
    },
    {
      title: t('admin_nav_income_cert'),
      total: stats?.total_income_certs || 0,
      pending: stats?.pending_income_certs || 0,
      icon: FileText,
      color: 'text-teal-600',
      bg: 'bg-teal-500/10',
      link: '/admin/income-certificates'
    },
    {
      title: t('admin_nav_residence_card'),
      total: stats?.total_residence_cards || 0,
      pending: stats?.pending_residence_cards || 0,
      icon: CreditCard,
      color: 'text-indigo-600',
      bg: 'bg-indigo-500/10',
      link: '/admin/residence-cards'
    },
    {
      title: t('admin_nav_balance_cert'),
      total: stats?.total_balance_certs || 0,
      pending: 0,
      icon: FileBadge,
      color: 'text-rose-600',
      bg: 'bg-rose-500/10',
      link: '/admin/balance-certificates'
    },
  ];

  const otherCards = [
    {
      title: t('admin_nav_aml'),
      value: stats?.active_aml_flags || 0,
      label: t('admin_dash_aml_open'),
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-500/10',
      link: '/admin/aml'
    },
    {
      title: t('admin_nav_referrals'),
      value: stats?.total_referrals || 0,
      label: t('admin_dash_referral_uses'),
      icon: Gift,
      color: 'text-pink-600',
      bg: 'bg-pink-500/10',
      link: '/admin/referrals'
    },
    {
      title: t('admin_nav_email'),
      value: stats?.total_email_accounts || 0,
      label: t('admin_dash_email_accounts'),
      icon: Mail,
      color: 'text-sky-600',
      bg: 'bg-sky-500/10',
      link: '/admin/email'
    },
    {
      title: t('admin_dash_recipients'),
      value: stats?.total_recipients || 0,
      label: t('admin_dash_registered'),
      icon: UserCheck,
      color: 'text-green-600',
      bg: 'bg-green-500/10',
      link: '/admin/users'
    },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('admin_dash_title')}</h1>
          <p className="text-muted-foreground">{t('admin_dash_subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="space-y-8">

            {/* Main Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mainCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Link key={index} to={stat.link} className={`bg-card border ${stat.border} rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group`} data-testid={`stat-card-${index}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${stat.color}`} />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-2xl font-bold font-heading text-primary">{stat.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{stat.title}</div>
                    {stat.sub && (
                      <div className={`text-xs mt-1.5 font-medium ${stat.subColor} flex items-center gap-1`}>
                        <Clock className="h-3 w-3" />
                        {stat.sub}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Alerts */}
            {(stats?.pending_kyc > 0 || stats?.pending_deposits > 0 || stats?.active_aml_flags > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats?.pending_kyc > 0 && (
                  <Link to="/admin/kyc" className="flex items-center gap-3 bg-amber-500/10 border border-amber-300/40 rounded-xl p-4 hover:shadow-md transition-all" data-testid="alert-kyc">
                    <FileCheck className="h-5 w-5 text-amber-600 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-amber-900">{t('admin_dash_pending_kyc_alert', { count: stats.pending_kyc })}</div>
                      <div className="text-xs text-amber-700">{t('admin_dash_pending_kyc_desc')}</div>
                    </div>
                  </Link>
                )}
                {stats?.pending_deposits > 0 && (
                  <Link to="/admin/deposits" className="flex items-center gap-3 bg-cyan-500/10 border border-cyan-300/40 rounded-xl p-4 hover:shadow-md transition-all" data-testid="alert-deposits">
                    <Wallet className="h-5 w-5 text-cyan-600 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-cyan-900">{stats.pending_deposits} {t('admin_dash_pending_deposits')}</div>
                      <div className="text-xs text-cyan-700">{t('admin_dash_pending_deposits_desc')}</div>
                    </div>
                  </Link>
                )}
                {stats?.active_aml_flags > 0 && (
                  <Link to="/admin/aml" className="flex items-center gap-3 bg-red-500/10 border border-red-300/40 rounded-xl p-4 hover:shadow-md transition-all" data-testid="alert-aml">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-red-900">{stats.active_aml_flags} {t('admin_dash_aml_alert')}</div>
                      <div className="text-xs text-red-700">{t('admin_dash_aml_alert_desc')}</div>
                    </div>
                  </Link>
                )}
              </div>
            )}

            {/* Document Section */}
            <div>
              <h2 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-muted-foreground" />
                {t('admin_dash_documents')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {docCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <Link key={index} to={card.link} className="bg-card border border-border/50 rounded-xl p-4 hover:shadow-md hover:border-accent/30 transition-all group" data-testid={`doc-card-${index}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                          <Icon className={`h-4 w-4 ${card.color}`} />
                        </div>
                        <span className="text-sm font-medium text-primary">{card.title}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <div className="text-xl font-bold text-primary">{card.total}</div>
                          <div className="text-xs text-muted-foreground">{t('total')}</div>
                        </div>
                        {card.pending > 0 && (
                          <div className="text-right">
                            <div className="text-lg font-bold text-amber-600">{card.pending}</div>
                            <div className="text-xs text-amber-600 flex items-center gap-1">
                              <CircleDot className="h-3 w-3" />
                              {t('pending')}
                            </div>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Other Management */}
            <div>
              <h2 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                {t('admin_dash_management')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {otherCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <Link key={index} to={card.link} className="bg-card border border-border/50 rounded-xl p-4 hover:shadow-md hover:border-accent/30 transition-all group" data-testid={`mgmt-card-${index}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                          <Icon className={`h-4 w-4 ${card.color}`} />
                        </div>
                        <span className="text-sm font-medium text-primary">{card.title}</span>
                      </div>
                      <div className="text-xl font-bold text-primary">{card.value}</div>
                      <div className="text-xs text-muted-foreground">{card.label}</div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Transfers */}
              <div className="bg-card border border-border/50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                    <Send className="h-4 w-4 text-emerald-600" />
                    {t('admin_dash_recent_transfers')}
                  </h3>
                  <Link to="/admin/transfers" className="text-xs text-accent hover:underline">{t('view_all')}</Link>
                </div>
                {stats?.recent_transfers?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_transfers.map((tr, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0" data-testid={`recent-transfer-${i}`}>
                        <div>
                          <div className="text-sm font-medium text-primary">{tr.recipient_name || '-'}</div>
                          <div className="text-xs text-muted-foreground">{tr.created_at ? new Date(tr.created_at).toLocaleDateString('ja-JP') : '-'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-primary">¥{(tr.total_jpy || 0).toLocaleString()}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            tr.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            tr.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{tr.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground">{t('no_data')}</div>
                )}
              </div>

              {/* Recent Users */}
              <div className="bg-card border border-border/50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    {t('admin_dash_recent_users')}
                  </h3>
                  <Link to="/admin/users" className="text-xs text-accent hover:underline">{t('view_all')}</Link>
                </div>
                {stats?.recent_users?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_users.map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0" data-testid={`recent-user-${i}`}>
                        <div>
                          <div className="text-sm font-medium text-primary">{u.full_name || u.email}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            u.kyc_status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            u.kyc_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{u.kyc_status}</span>
                          <div className="text-xs text-muted-foreground mt-1">{u.created_at ? new Date(u.created_at).toLocaleDateString('ja-JP') : '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground">{t('no_data')}</div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </AdminLayout>
  );
}
